const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : "http://localhost:5000/api";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  ...authHeaders(),
});

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      message = data.message || data.error || message;
    } catch {
      // ignore parse fallback
    }
    throw new Error(message);
  }
  return res.json();
}

const toDate = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toISOString().slice(0, 10);
};

export interface MemberRow {
  id: number;
  memberId: string;
  fullName: string;
  mobile: string;
  email: string;
  aadhar?: string;
  referenceBy?: string;
  gender: string;
  fathersName?: string;
  state: string;
  city: string;
  pinCode?: string;
  address?: string;
  nationality?: string;
  qualification?: string;
  areaOfOperation?: string;
  experience?: string;
  briefAboutYourself?: string;
  photoUrl?: string;
  resumeUrl?: string;
  photoFile?: File;
  resumeFile?: File;
  createdAt?: string;
  dateOfBirth?: string;
}

export interface VolunteerRow {
  id: number;
  volunteerId: string;
  fullName: string;
  mobile: string;
  email: string;
  aadhar?: string;
  gender?: string;
  dateOfBirth?: string;
  pinCode?: string;
  address?: string;
  skills: string;
  otherSkill?: string;
  availability: string;
  volunteeringArea?: string;
  previousExperience?: string;
  experienceDetails?: string;
  photoUrl?: string;
  photoFile?: File;
  state: string;
  city: string;
}

export interface DonationRow {
  id: number;
  receiptNo?: string;
  fullName: string;
  mobile: string;
  email?: string;
  country?: string;
  state?: string;
  city?: string;
  pinCode?: string;
  address?: string;
  panNo?: string;
  amount: number;
  paymentMethod: string;
  message?: string;
  photoUrl?: string;
  photoFile?: File;
  preARN?: string;
  certificateNo?: string;
  createdAt?: string;
  dateOfBirth?: string;
}

export interface BlogRow {
  id: number;
  title: string;
  description: string;
  imageUrl?: string | null;
  imageFile?: File;
  createdAt?: string;
}

export interface NotificationRow {
  id: number;
  message: string;
  createdAt?: string;
}

export interface EventRow {
  id: number;
  title: string;
  description?: string;
  imageUrl?: string;
  createdAt?: string;
}

export interface TestimonialRow {
  id: number;
  name: string;
  text: string;
  created_at?: string;
  updated_at?: string;
}

export const fetchMembers = async () => {
  const res = await fetch(`${API_BASE}/members`, { headers: authHeaders() });
  const data = await parseJson<MemberRow[]>(res);
  return data.map((m) => ({ ...m, createdAt: toDate(m.createdAt || m.dateOfBirth) }));
};

export const fetchVolunteers = async () => {
  const res = await fetch(`${API_BASE}/volunteers`, { headers: authHeaders() });
  const data = await parseJson<VolunteerRow[]>(res);
  return data;
};

export const fetchDonations = async () => {
  const res = await fetch(`${API_BASE}/donations`);
  const data = await parseJson<DonationRow[]>(res);
  return data.map((d) => ({ ...d, createdAt: toDate(d.createdAt || d.dateOfBirth) }));
};

export const fetchBlogs = async () => {
  const res = await fetch(`${API_BASE}/blogs`);
  const data = await parseJson<BlogRow[]>(res);
  return data.map((b) => ({ ...b, createdAt: toDate(b.createdAt) }));
};

export const fetchNotificationsCount = async () => {
  const res = await fetch(`${API_BASE}/notifications`, { headers: authHeaders() });
  const data = await parseJson<any[]>(res);
  return data.length;
};

export const fetchEventsCount = async () => {
  const res = await fetch(`${API_BASE}/event-slides`, { headers: authHeaders() });
  const data = await parseJson<any[]>(res);
  return data.length;
};

export const fetchTestimonialsCount = async () => {
  const res = await fetch(`${API_BASE}/testimonials`, { headers: authHeaders() });
  const data = await parseJson<any[]>(res);
  return data.length;
};

export const fetchNotifications = async () => {
  const res = await fetch(`${API_BASE}/notifications`, { headers: authHeaders() });
  const data = await parseJson<NotificationRow[]>(res);
  return data;
};

export const createNotification = async (message: string) => {
  const res = await fetch(`${API_BASE}/notifications`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ message }),
  });
  return parseJson<{ message: string }>(res);
};

export const updateNotification = async (id: number, message: string) => {
  const res = await fetch(`${API_BASE}/notifications/${id}`, {
    method: "PUT",
    headers: jsonHeaders(),
    body: JSON.stringify({ message }),
  });
  return parseJson<{ message: string }>(res);
};

export const deleteNotification = async (id: number) => {
  const res = await fetch(`${API_BASE}/notifications/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJson<{ message: string }>(res);
};

export const fetchEvents = async () => {
  const res = await fetch(`${API_BASE}/event-slides`, { headers: authHeaders() });
  const data = await parseJson<EventRow[]>(res);
  return data;
};

export const fetchTestimonials = async () => {
  const res = await fetch(`${API_BASE}/testimonials`, { headers: authHeaders() });
  const data = await parseJson<TestimonialRow[]>(res);
  return data;
};

export const createEvent = async (title: string, description: string, image?: File | null) => {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("description", description);
  if (image) {
    formData.append("image", image);
  }

  const res = await fetch(`${API_BASE}/event-slides`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  return parseJson<{ message: string; imageUrl?: string }>(res);
};

export const updateEvent = async (id: number, title: string, description: string, image?: File | null, imageUrl?: string) => {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("description", description);
  if (image) {
    formData.append("image", image);
  } else if (imageUrl) {
    formData.append("imageUrl", imageUrl);
  }

  const res = await fetch(`${API_BASE}/event-slides/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: formData,
  });
  return parseJson<{ message: string; imageUrl?: string }>(res);
};

export const deleteEvent = async (id: number) => {
  const res = await fetch(`${API_BASE}/event-slides/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJson<{ message: string }>(res);
};

export const createTestimonial = async (name: string, text: string) => {
  const res = await fetch(`${API_BASE}/testimonials`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, text }),
  });
  return parseJson<{ message: string; testimonial: TestimonialRow }>(res);
};

export const updateTestimonial = async (id: number, name: string, text: string) => {
  const res = await fetch(`${API_BASE}/testimonials/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, text }),
  });
  return parseJson<{ message: string; testimonial: TestimonialRow }>(res);
};

export const deleteTestimonial = async (id: number) => {
  const res = await fetch(`${API_BASE}/testimonials/${id}`, {
    method: "DELETE",
  });
  return parseJson<{ message: string }>(res);
};

export const deleteMember = async (id: number) => {
  const res = await fetch(`${API_BASE}/members/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJson<{ message: string }>(res);
};

export const deleteVolunteer = async (id: number) => {
  const res = await fetch(`${API_BASE}/volunteers/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJson<{ message: string }>(res);
};

export const deleteDonation = async (id: number) => {
  const res = await fetch(`${API_BASE}/donations/${id}`, { method: "DELETE" });
  return parseJson<{ message: string }>(res);
};

export const updateMember = async (row: MemberRow) => {
  const formData = new FormData();
  formData.append("memberId", row.memberId || "");
  formData.append("fullName", row.fullName || "");
  formData.append("mobile", row.mobile || "");
  formData.append("email", row.email || "");
  formData.append("aadhar", row.aadhar || "");
  formData.append("referenceBy", row.referenceBy || "");
  formData.append("gender", row.gender || "");
  formData.append("dateOfBirth", row.dateOfBirth || "");
  formData.append("fathersName", row.fathersName || "");
  formData.append("state", row.state || "");
  formData.append("city", row.city || "");
  formData.append("pinCode", row.pinCode || "");
  formData.append("address", row.address || "");
  formData.append("nationality", row.nationality || "");
  formData.append("qualification", row.qualification || "");
  formData.append("areaOfOperation", row.areaOfOperation || "");
  formData.append("experience", row.experience || "");
  formData.append("briefAboutYourself", row.briefAboutYourself || "");
  if (row.photoFile) {
    formData.append("photo", row.photoFile);
  } else {
    formData.append("photoUrl", row.photoUrl || "");
  }
  if (row.resumeFile) {
    formData.append("resume", row.resumeFile);
  } else {
    formData.append("resumeUrl", row.resumeUrl || "");
  }

  const res = await fetch(`${API_BASE}/members/${row.id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: formData,
  });
  return parseJson<{ message: string }>(res);
};

export const updateVolunteer = async (row: VolunteerRow) => {
  const formData = new FormData();
  formData.append("fullName", row.fullName || "");
  formData.append("mobile", row.mobile || "");
  formData.append("email", row.email || "");
  formData.append("aadhar", row.aadhar || "");
  formData.append("gender", row.gender || "");
  formData.append("dateOfBirth", row.dateOfBirth || "");
  formData.append("state", row.state || "");
  formData.append("city", row.city || "");
  formData.append("pinCode", row.pinCode || "");
  formData.append("address", row.address || "");
  formData.append("skills", row.skills || "");
  formData.append("otherSkill", row.otherSkill || "");
  formData.append("availability", row.availability || "");
  formData.append("volunteeringArea", row.volunteeringArea || "");
  formData.append("previousExperience", row.previousExperience || "");
  formData.append("experienceDetails", row.experienceDetails || "");
  if (row.photoFile) {
    formData.append("photo", row.photoFile);
  } else {
    formData.append("photoUrl", row.photoUrl || "");
  }

  const res = await fetch(`${API_BASE}/volunteers/${row.id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: formData,
  });
  return parseJson<{ message: string }>(res);
};

export const updateDonation = async (row: DonationRow) => {
  const formData = new FormData();
  formData.append("fullName", row.fullName || "");
  formData.append("mobile", row.mobile || "");
  formData.append("email", row.email || "");
  formData.append("dateOfBirth", row.dateOfBirth || "");
  formData.append("country", row.country || "");
  formData.append("state", row.state || "");
  formData.append("city", row.city || "");
  formData.append("pinCode", row.pinCode || "");
  formData.append("address", row.address || "");
  formData.append("panNo", row.panNo || "");
  formData.append("amount", String(row.amount || 0));
  formData.append("paymentMethod", row.paymentMethod || "");
  formData.append("message", row.message || "");
  if (row.photoFile) {
    formData.append("photo", row.photoFile);
  } else {
    formData.append("photoUrl", row.photoUrl || "");
  }
  formData.append("receiptNo", row.receiptNo || "");
  formData.append("preARN", row.preARN || "");
  formData.append("certificateNo", row.certificateNo || "");

  const res = await fetch(`${API_BASE}/donations/${row.id}`, {
    method: "PUT",
    body: formData,
  });
  return parseJson<{ message: string; donation: DonationRow }>(res);
};

export const createBlog = async (title: string, description: string, image: File) => {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("description", description);
  formData.append("image", image);

  const res = await fetch(`${API_BASE}/blogs`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  return parseJson<{ message: string }>(res);
};

export const deleteBlog = async (id: number) => {
  const res = await fetch(`${API_BASE}/blogs/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJson<{ message: string }>(res);
};

export const updateBlog = async (row: BlogRow) => {
  const formData = new FormData();
  formData.append("title", row.title || "");
  formData.append("description", row.description || "");
  formData.append("currentImageUrl", row.imageUrl || "");
  if (row.imageFile) {
    formData.append("image", row.imageFile);
  }

  const res = await fetch(`${API_BASE}/blogs/${row.id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: formData,
  });
  return parseJson<{ message: string; blog: BlogRow }>(res);
};

export const changePassword = async (oldPassword: string, newPassword: string) => {
  const res = await fetch(`${API_BASE}/auth/change-password`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ oldPassword, newPassword }),
  });
  return parseJson<{ message: string }>(res);
};
